import { motion } from "framer-motion";
import { ShieldCheck, Clock, BadgeCheck, ArrowRight, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { COMPANY, WHATSAPP_URL } from "@/lib/constants";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { trackConversion } from "@/lib/tracking";

const ease = [0.16, 1, 0.3, 1];

export const QuoteForm = () => {
  return (
    <section
      id="devis"
      data-testid="quote-section"
      className="bg-charcoal-deep py-24 sm:py-32 lg:py-40 relative overflow-hidden"
    >
      {/* Atmospheric glows */}
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-bronze/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-bronze/5 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#25D366]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* LEFT — copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease }}
            className="lg:col-span-5"
          >
            <span className="text-bronze text-xs uppercase tracking-[0.25em] font-medium">
              Demander un devis
            </span>
            <h2 className="mt-4 font-serif text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-normal">
              <span className="sr-only">Demandez Votre Devis Gratuit de Rénovation à Genève. </span>
              Parlons de votre projet.
              <br />
              <span className="italic text-bronze-light">Sans engagement.</span>
            </h2>
            <p className="mt-8 text-white/70 text-base lg:text-lg leading-relaxed font-light max-w-md">
              Un membre de la famille Demiri vous répond personnellement sur WhatsApp.
              Photos, plan, idées : envoyez-nous tout ce que vous avez. On revient vers vous
              avec un premier chiffrage sous 24h.
            </p>

            <div className="mt-12 pt-10 border-t border-white/10 space-y-4 text-white/80 text-sm">
              <div className="flex items-start gap-3">
                <span className="text-bronze">·</span>
                <span>{COMPANY.address}, {COMPANY.city}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-bronze">·</span>
                <a href={`tel:${COMPANY.phoneRaw}`} onClick={trackConversion(`tel:${COMPANY.phoneRaw}`)} className="hover:text-bronze transition-colors">
                  {COMPANY.phone}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-bronze">·</span>
                <a href={`mailto:${COMPANY.email}`} className="hover:text-bronze transition-colors">
                  {COMPANY.email}
                </a>
              </div>
            </div>
          </motion.div>

          {/* RIGHT — WhatsApp CTA card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.9, ease, delay: 0.2 }}
            className="lg:col-span-7"
          >
            <div
              data-testid="whatsapp-card"
              className="bg-white/[0.03] border border-white/10 backdrop-blur-sm p-8 sm:p-10 lg:p-14 space-y-10"
            >
              {/* Reassurance icons */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pb-8 border-b border-white/10">
                <div className="flex items-center gap-3 text-white/85">
                  <ShieldCheck className="w-5 h-5 text-bronze flex-shrink-0" strokeWidth={1.25} />
                  <span className="text-xs leading-tight">Gratuit & sans engagement</span>
                </div>
                <div className="flex items-center gap-3 text-white/85">
                  <Clock className="w-5 h-5 text-bronze flex-shrink-0" strokeWidth={1.25} />
                  <span className="text-xs leading-tight">Réponse sous 24h</span>
                </div>
                <div className="flex items-center gap-3 text-white/85">
                  <BadgeCheck className="w-5 h-5 text-bronze flex-shrink-0" strokeWidth={1.25} />
                  <span className="text-xs leading-tight">Garantie sur tous les travaux</span>
                </div>
              </div>

              {/* WhatsApp main CTA */}
              <div className="text-center space-y-6">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#25D366]/15 border border-[#25D366]/30">
                  <WhatsAppIcon className="w-10 h-10 text-[#25D366]" />
                </div>

                <div>
                  <h3 className="font-serif text-white text-3xl sm:text-4xl font-normal leading-tight">
                    Discutons sur <span className="italic text-bronze-light">WhatsApp.</span>
                  </h3>
                  <p className="mt-4 text-white/65 text-sm sm:text-base font-light max-w-md mx-auto leading-relaxed">
                    Plus simple, plus rapide. Envoyez-nous photos, vidéos ou idées. On vous
                    répond en moyenne sous 1h pendant les heures de bureau.
                  </p>
                </div>

                <a
                  href={WHATSAPP_URL}
                  onClick={trackConversion(WHATSAPP_URL)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-cta-main"
                  className="group inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1ebe5a] text-white w-full sm:w-auto px-10 py-5 text-xs uppercase tracking-[0.25em] font-medium transition-all duration-300 shadow-[0_8px_24px_rgba(37,211,102,0.35)] hover:shadow-[0_12px_32px_rgba(37,211,102,0.5)]"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                  Écrire sur WhatsApp
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                </a>

                <div className="flex items-center gap-4 pt-2">
                  <span className="flex-1 h-px bg-white/10" />
                  <span className="text-white/40 text-[10px] tracking-[0.3em] uppercase">Ou par téléphone</span>
                  <span className="flex-1 h-px bg-white/10" />
                </div>

                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  onClick={trackConversion(`tel:${COMPANY.phoneRaw}`)}
                  data-testid="quote-call-cta"
                  className="group inline-flex items-center justify-center gap-3 border border-white/20 hover:border-bronze hover:bg-bronze/5 text-white w-full sm:w-auto px-10 py-4 text-xs uppercase tracking-[0.25em] transition-all duration-300"
                >
                  <Phone className="w-4 h-4 text-bronze" strokeWidth={1.5} />
                  Appeler {COMPANY.phone}
                </a>
              </div>

              <p className="text-white/40 text-xs font-light text-center leading-relaxed border-t border-white/10 pt-6">
                Aucun démarchage. Vos coordonnées restent confidentielles.
                <br />
                <Link
                  to="/candidatures"
                  data-testid="quote-candidatures-link"
                  className="inline-block mt-2 text-white/60 hover:text-white underline underline-offset-4 transition-colors"
                >
                  Vous cherchez un emploi ? Candidatures ici
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
